import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { setReactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import sanitizeHtml from 'sanitize-html'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { countSpecialChars } from '../../../components/ContentEditor/contentUtils'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import {
    cancellingPills,
    insertingUOMViaKeyboardPills,
    patchUOMChangeType,
    pillInlineTextBeforeUOM,
    pillTextCache,
    UOM_MAX_LENGTH,
    UOM_MAX_SPECIAL_CHARS,
    updateSOInNodes,
} from '../uomPillState'

// pillId → Map<uomId, {imperial, metric}>
// Module-level so multiple UOM instances on the same pill share the same cache.
const _pillUOMCache = new Map<string, Map<string, { imperial: string; metric: string }>>()

// Dedup guard: the first saveAll call for a pill wins; siblings skip.
const _savingPills = new Set<string>()

// Strips markup from a raw UOM string and enforces the UOM limits. Returns null when the
// value is not acceptable so callers can fall back to the last known-good value.
//
// Both the keystroke path and the blur path must run this. UOMPillInput is uncontrolled, so
// a keystroke rejected here still sits in the DOM — reading that DOM value back without
// re-validating would persist exactly the input the limits were meant to reject.
const sanitizeUOMText = (raw: string): string | null => {
    const sanitized = sanitizeHtml(raw.trim(), { allowedTags: [], allowedAttributes: {} })
    const value = new DOMParser().parseFromString(sanitized, 'text/html').body.textContent ?? ''
    if (value.length > UOM_MAX_LENGTH || countSpecialChars(value) > UOM_MAX_SPECIAL_CHARS)
        return null
    return value
}

// Recursively finds an element by id within an element tree. Mirrors how
// ContentItemInputBuilder locates the element, so change detection, the optimistic update
// and the mutation payload all agree on where the UOM lives.
const findElementById = (
    elements: Partial<TextContentElement>[] | undefined,
    id: string
): Partial<TextContentElement> | null => {
    for (const el of elements ?? []) {
        if (el.id === id) return el
        const nested = findElementById(el.elements, id)
        if (nested) return nested
    }
    return null
}

// Returns a copy of the tree with the element matching `id` replaced. Structurally shares
// every branch that does not contain the target.
const replaceElementById = (
    elements: Partial<TextContentElement>[] | undefined,
    id: string,
    replacement: Partial<TextContentElement>
): Partial<TextContentElement>[] =>
    (elements ?? []).map((el) => {
        if (el.id === id) return replacement
        if (!findElementById(el.elements, id)) return el
        return { ...el, elements: replaceElementById(el.elements, id, replacement) }
    })

interface UseUOMSaveOptions {
    contentItem: Partial<TextContentElement>
    parentContentItem: Partial<TextContentElement> | undefined
    pillRef: React.RefObject<HTMLElement> | undefined
    onCancel: (() => void) | undefined
    imperialUnits: string
    metricUnits: string
    setContentItemViewNodes: (nodes: Partial<ContentItemViewNodesType>[]) => void
    mutateContentElements: (id: string, elements: Partial<TextContentElement>[]) => Promise<unknown>
    executeWithOptimistic: (opts: {
        fallbackState: string
        asyncAction: () => Promise<unknown>
        successCbFn: () => void
    }) => void
    fetchTrackChangesDataWithApolloClient: () => void
}

export const useUOMSave = ({
    contentItem,
    parentContentItem,
    pillRef,
    onCancel,
    imperialUnits,
    metricUnits,
    setContentItemViewNodes,
    mutateContentElements,
    executeWithOptimistic,
    fetchTrackChangesDataWithApolloClient,
}: UseUOMSaveOptions) => {
    const [localImperial, setLocalImperial] = useState(imperialUnits)
    const [localMetric, setLocalMetric] = useState(metricUnits)
    const localImperialRef = useRef(imperialUnits)
    const localMetricRef = useRef(metricUnits)
    const imperialContainerRef = useRef<HTMLDivElement | null>(null)
    const metricContainerRef = useRef<HTMLDivElement | null>(null)

    // Sync local display state when the store value changes externally (e.g. after save or remote update)
    useEffect(() => {
        setLocalImperial(imperialUnits)
        localImperialRef.current = imperialUnits
        setLocalMetric(metricUnits)
        localMetricRef.current = metricUnits
    }, [imperialUnits, metricUnits])

    // Normal UOM save. Runs once per blur — ReplacementOption's controller
    // calls onValueChange only from handleBlur (per-keystroke change events update
    // internal unsavedData only), so no debounce is required.
    const handleNormalUOMChange = useCallback(
        (
            updatedItem: Partial<TextContentElement>,
            unitType: 'imperial' | 'metric'
        ) => {
            if (!contentItem.id || !parentContentItem?.id) return

            const newValue = sanitizeUOMText(updatedItem.text ?? '')
            if (newValue === null) return

            const updatedUOM: Partial<TextContentElement> = {
                ...contentItem,
                ...(unitType === 'imperial'
                    ? { imperialUnits: newValue }
                    : { metricUnits: newValue }),
                changeType: 'MODIFIED',
            }

            // Read current nodes at execution time to avoid stale closure
            const currentNodes = useWorkspace2Store.getState()
                .contentItemViewNodes

            // Optimistic update via replaceElementById so nested UOMs
            // (node → OptionGroup → SelectionOption → UOM) are found — a
            // one-level `node.elements` walk would silently no-op there.
            setContentItemViewNodes(
                currentNodes.map((node) =>
                    node.id === parentContentItem.id
                        ? {
                              ...node,
                              elements: replaceElementById(
                                  node.elements,
                                  contentItem.id!,
                                  updatedUOM
                              ),
                          }
                        : node
                )
            )

            // executeWithOptimistic handles "Saving..." / "Changes Saved Successfully" toasts
            // and rollback on error via its internal transaction snapshot — no errorCbFn needed.
            executeWithOptimistic({
                fallbackState: '',
                asyncAction: () =>
                    mutateContentElements(contentItem.id!, [updatedUOM]),
                successCbFn: () => {
                    fetchTrackChangesDataWithApolloClient()
                },
            })
        },
        [
            contentItem,
            parentContentItem,
            setContentItemViewNodes,
            mutateContentElements,
            executeWithOptimistic,
            fetchTrackChangesDataWithApolloClient,
        ]
    )

    // Update local display state only — store/API are deferred until focus leaves the pill.
    const handleUOMLocalChange = useCallback(
        (updatedItem: Partial<TextContentElement>, unitType: 'imperial' | 'metric') => {
            if (!contentItem.id || !parentContentItem?.id) return
            const newValue = sanitizeUOMText(updatedItem.text ?? '')
            if (newValue === null) return
            if (unitType === 'imperial') {
                setLocalImperial(newValue)
                localImperialRef.current = newValue
            } else {
                setLocalMetric(newValue)
                localMetricRef.current = newValue
            }
            // Write to module-level cache so saveAll can read sibling UOM values
            // even if their own focusout handlers are removed mid-dispatch.
            const uomCacheForPill = _pillUOMCache.get(parentContentItem.id!) ?? new Map()
            const existing = uomCacheForPill.get(contentItem.id!) ?? {
                imperial: localImperialRef.current,
                metric: localMetricRef.current,
            }
            uomCacheForPill.set(
                contentItem.id!,
                unitType === 'imperial'
                    ? { imperial: newValue, metric: existing.metric }
                    : { imperial: existing.imperial, metric: newValue }
            )
            _pillUOMCache.set(parentContentItem.id!, uomCacheForPill)
        },
        [contentItem.id, parentContentItem?.id]
    )

    // Persist all pending UOM and text changes to the store and API.
    // Called when focus truly leaves the pill via the focusout listener below.
    const saveAll = useCallback(() => {
        if (!contentItem.id || !parentContentItem?.id) return
        const pillId = parentContentItem.id!
        if (_savingPills.has(pillId)) return
        _savingPills.add(pillId)
        queueMicrotask(() => _savingPills.delete(pillId))

        // focusout fires before CKEditor's own blur, so localRefs may not reflect the
        // latest typed value when this UOM's editor was still active. Read from the DOM
        // via refs directly as the authoritative source, falling back to the ref if not present.
        const getActiveEditorText = (type: 'imperial' | 'metric'): string | null => {
            const container = (type === 'imperial' ? imperialContainerRef : metricContainerRef).current
            if (!container) return null
            const editable = container.querySelector('.ck-editor__editable')
            if (editable) return (editable.textContent ?? '').trim()
            // Plain-input path (UOMPillInput): no CKEditor mounts, so read the input value.
            const pillInput = container.querySelector<HTMLInputElement>('[data-uom-pill-input]')
            return pillInput ? pillInput.value.trim() : null
        }
        // The DOM is the freshest source but an unvalidated one — it still holds keystrokes
        // that sanitizeUOMText rejected. Re-validate, and fall back to the ref (which only
        // ever holds validated values) when the DOM value is not acceptable.
        const readCurrent = (type: 'imperial' | 'metric', ref: React.MutableRefObject<string>) => {
            const domText = getActiveEditorText(type)
            return (domText !== null ? sanitizeUOMText(domText) : null) ?? ref.current
        }
        const currentImperial = readCurrent('imperial', localImperialRef)
        const currentMetric = readCurrent('metric', localMetricRef)

        const currentNodes = useWorkspace2Store.getState().contentItemViewNodes

        // UOM is nested: paragraph node → elements → OptionGroup → elements → SelectionOption → elements → UOM
        let storeUOM: Partial<TextContentElement> | null = null
        let storeSO: Partial<TextContentElement> | null = null
        for (const node of currentNodes) {
            for (const ogEl of node.elements ?? []) {
                if (ogEl.type !== 'OptionGroup') continue
                const so = (ogEl.elements ?? []).find((el) => el.id === parentContentItem.id)
                if (so) {
                    storeSO = so
                    storeUOM = (so.elements ?? []).find((el) => el.id === contentItem.id) ?? null
                    break
                }
            }
            if (storeSO !== null) break
        }

        // Standalone UOM (rendered directly by NodeContentRenderer, not inside a pill):
        // parentContentItem is that node's own contentItemView, so its id matches node.id.
        // Resolve it here — without this storeUOM stays null and uomChanged below is
        // unconditionally true, so a click-in/click-out with no edit would save.
        const standaloneNode = storeSO
            ? undefined
            : currentNodes.find((n) => n.id === parentContentItem.id)
        if (standaloneNode) {
            storeUOM = findElementById(standaloneNode.elements, contentItem.id)
        }

        // Read text values from the module-level cache written per-keystroke by SelectionOption's
        // text inputs. Avoids per-keystroke store updates (no re-render delay) and avoids
        // DOM querying which misses inputs outside the pill's <p> element boundary.
        const domTextValues = pillTextCache.get(parentContentItem.id!) ?? new Map<string, string>()

        const uomChanged =
            !storeUOM ||
            currentImperial !== storeUOM.imperialUnits ||
            currentMetric !== storeUOM.metricUnits
        const textChanged = [...domTextValues.entries()].some(([id, val]) => {
            const el = (storeSO?.elements ?? []).find((e) => e.id === id)
            return el?.text !== val
        })

        if (!uomChanged && !textChanged) {
            // Standalone UOM has no pill to cancel and no inline editors to unmount. Bail before
            // the checks below: they read storeSO.elements, and .every() on the empty fallback
            // array is vacuously true, which would add the id to cancellingPills and make every
            // subsequent focusout return early — so the UOM would never save again.
            if (!storeSO) return
            // If every element is empty the user never filled anything in —
            // treat it like Escape so the pill is removed entirely.
            const allEmpty = (storeSO?.elements ?? []).every(
                (el) =>
                    (el.type === 'Text' && !el.text) ||
                    (el.type === 'UnitsOfMeasure' && !el.imperialUnits && !el.metricUnits)
            )
            if (allEmpty) {
                if (parentContentItem?.id) cancellingPills.add(parentContentItem.id)
                onCancel?.()
                return
            }
            // Transition ALL ADDED UOMs in this SO → MODIFIED so uomChildrenToRender
            // becomes empty and all inline editors unmount. Only patch changeType — do NOT
            // rebuild the full node tree, which would overwrite Text siblings
            // with their empty store values and make the pill appear blank.
            const hasAddedUOMs = (storeSO?.elements ?? []).some(
                (el) => el.type === 'UnitsOfMeasure' && el.changeType === 'ADDED'
            )
            if (hasAddedUOMs) {
                setContentItemViewNodes(patchUOMChangeType(currentNodes, parentContentItem.id!))
            }
            return
        }

        const updatedUOM: Partial<TextContentElement> = {
            ...(storeUOM ?? contentItem),
            imperialUnits: currentImperial,
            metricUnits: currentMetric,
            ...(uomChanged ? { changeType: 'MODIFIED' as const } : undefined),
        }

        // Standalone UOM: no SelectionOption ancestor, so updateSOInNodes below matches nothing
        // and the optimistic update would silently no-op. Replace the element on its own node
        // and persist directly; the mutation builder finds the owning node by recursive search.
        if (!storeSO) {
            // No node means no baseline: uomChanged above falls back to `!storeUOM`, which is
            // unconditionally true, so saving here would fire a mutation for a blur that may
            // carry no edit at all — the spurious save this fix exists to prevent. It also
            // means parentContentItem is stale relative to the store, so the value itself is
            // not trustworthy. Bail rather than persist.
            if (!standaloneNode) return
            setContentItemViewNodes(
                currentNodes.map((node) =>
                    node.id === standaloneNode.id
                        ? {
                              ...node,
                              elements: replaceElementById(
                                  node.elements,
                                  contentItem.id!,
                                  updatedUOM
                              ),
                          }
                        : node
                )
            )
            executeWithOptimistic({
                fallbackState: '',
                asyncAction: () => mutateContentElements(contentItem.id!, [updatedUOM]),
                successCbFn: () => fetchTrackChangesDataWithApolloClient(),
            })
            _pillUOMCache.delete(pillId)
            return
        }

        let pillAllEmpty = false
        let uomIsInTrimmed = true
        let trimmedElements: Partial<TextContentElement>[] = []
        const updatedNodes = updateSOInNodes(currentNodes, parentContentItem.id!, (so) => {
            const mapped = (so.elements ?? []).map((el) => {
                if (el.id === contentItem.id) return updatedUOM
                const domText = domTextValues.get(el.id!)
                if (domText !== undefined) return { ...el, text: domText }
                // Sibling ADDED UOM: read latest values from cache and transition
                // to MODIFIED so uomChildrenToRender empties and the pill converts
                // to plain text. focusout for sibling instances is blocked by
                // _savingPills, so this is the only place they get updated.
                if (el.type === 'UnitsOfMeasure' && el.changeType === 'ADDED') {
                    const cached = _pillUOMCache.get(parentContentItem.id!)?.get(el.id!)
                    return {
                        ...el,
                        ...(cached ? { imperialUnits: cached.imperial, metricUnits: cached.metric } : {}),
                        changeType: 'MODIFIED' as const,
                    }
                }
                return el
            })
            // Drop trailing unfilled elements so they are not persisted.
            const trimmed = [...mapped]
            while (trimmed.length > 0) {
                const last = trimmed[trimmed.length - 1]
                const isEmpty =
                    (last.type === 'Text' && !last.text) ||
                    (last.type === 'UnitsOfMeasure' && !last.imperialUnits && !last.metricUnits)
                if (isEmpty) trimmed.pop()
                else break
            }
            if (trimmed.length === 0) pillAllEmpty = true
            uomIsInTrimmed = trimmed.some((el) => el.id === contentItem.id)
            trimmedElements = trimmed as Partial<TextContentElement>[]
            return { ...so, elements: trimmed }
        })

        if (pillAllEmpty) {
            if (parentContentItem?.id) cancellingPills.add(parentContentItem.id)
            onCancel?.()
            return
        }

        setContentItemViewNodes(updatedNodes)

        // After trimming, the UOM may no longer be in the store. Use any remaining
        // element's ID as the anchor; the builder still walks the full trimmed tree.
        const mutationId = uomIsInTrimmed
            ? contentItem.id!
            : trimmedElements[0]?.id ?? contentItem.id!
        const mutationEl = uomIsInTrimmed ? updatedUOM : (trimmedElements[0] ?? updatedUOM)

        executeWithOptimistic({
            fallbackState: '',
            asyncAction: () => mutateContentElements(mutationId, [mutationEl]),
            successCbFn: () => fetchTrackChangesDataWithApolloClient(),
        })
        _pillUOMCache.delete(pillId)
    }, [
        contentItem.id,
        parentContentItem?.id,
        onCancel,
        setContentItemViewNodes,
        mutateContentElements,
        executeWithOptimistic,
        fetchTrackChangesDataWithApolloClient,
    ])

    useEffect(() => {
        const id = parentContentItem?.id
        return () => {
            if (id) {
                _pillUOMCache.delete(id)
                cancellingPills.delete(id)
                pillInlineTextBeforeUOM.delete(id)
            }
        }
    }, [parentContentItem?.id])

    // Keep refs so the focusout listener closure never goes stale without triggering
    // a listener re-attach (which would create a window where a click-away focusout
    // could be missed if it lands between cleanup and re-add).
    const saveAllRef = useRef(saveAll)
    saveAllRef.current = saveAll
    const pillIdRef = useRef(parentContentItem?.id ?? '')
    pillIdRef.current = parentContentItem?.id ?? ''

    // Attach a focusout listener to the pill DOM element. focusout bubbles, so this fires
    // whenever any descendant loses focus. When relatedTarget is outside the pill, focus has
    // truly left — commit all pending UOM changes.
    //
    // Depends only on pillRef (stable useRef identity) so the listener is never
    // re-attached mid-session; saveAll and pillId are accessed via refs instead.
    useEffect(() => {
        const pillEl = pillRef?.current
        if (!pillEl) return
        const handleFocusOut = (e: FocusEvent) => {
            const pillId = pillIdRef.current
            const relatedTarget = e.relatedTarget as Node | null
            const isInsidePill = pillEl.contains(relatedTarget)
            if (cancellingPills.has(pillId)) return
            if (!pillEl.isConnected) return
            if (insertingUOMViaKeyboardPills.has(pillId)) {
                // Ctrl+U insert in flight — this focusout is from React committing the insert,
                // not a click-away. Refocus the pill so a later real click-away still saves.
                requestAnimationFrame(() => {
                    if (document.activeElement === document.body) pillEl.focus()
                })
                return
            }
            if (!isInsidePill) {
                if (useWorkspace2Store.getState().isActionButtonMousedown) return
                setReactiveWorkspaceActions({
                    isInsertUOM: false,
                    omWorkspaceInsertUOMHandler: undefined,
                })
                saveAllRef.current()
            }
        }
        pillEl.addEventListener('focusout', handleFocusOut)
        return () => pillEl.removeEventListener('focusout', handleFocusOut)
    }, [pillRef])

    return {
        localImperial,
        localMetric,
        handleNormalUOMChange,
        handleUOMLocalChange,
        imperialContainerRef,
        metricContainerRef,
    }
}
